import React, { useEffect, useMemo, useState } from 'react';
import css from './styles.module.scss';
import { DealCard } from './components/deal-card';
import { Deal } from '~/features/dashboard/store/models';
import { ChipsFilter } from '~/common/chips-filter';
import { Search } from '~/common/search';
import {
    Box, ButtonBase, Divider, Drawer, IconButton, TextField, Typography, Grid,
} from '@material-ui/core';
import moment from 'moment';
import { DateRange, DateRangeDelimiter, DateRangePicker, LocalizationProvider } from '@material-ui/pickers-next';
import MomentUtils from '@material-ui/pickers-next/adapter/moment';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { getSearchDealFilterActions } from '../../store/actions';
import { useSelector } from 'react-redux';
import { searchDealFilter } from '../../store/selectors';
import { Pagination } from '@material-ui/lab';
import { useDateFormat } from '~/common/date-format';
import FilterListIcon from '@material-ui/icons/FilterList';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { ContactCard } from '~/features/dashboard/components/deals-grid/components/contact-card';
import { ContactsListItem } from '~/models/contacts.dto';
import { v4 } from 'uuid';

interface DealsGridProps {
    deals: Deal[];
}

enum DRAWER_TYPES {
    FILTER = 'FILTER',
}

export const DealsGrid: React.FC<DealsGridProps> = ({ deals }) => {
    const dispatch = useThunkDispatch();
    const { selectedDateFormat } = useDateFormat();

    const [dealTypesFilter, setDealTypesFilter] = useState<string[]>([]);
    const [dealStatusFilter, setDealStatusFilter] = useState<string[]>([]);
    const [dateFilter, setDateFilter] = React.useState<DateRange<Date>>([null, null]);
    const [searchValue, setSearchValue] = useState('');
    const getSearchDealValue = useSelector(searchDealFilter) || '';

    const [drawerData, setDrawerData] = useState<{ isOpen: boolean; type?: DRAWER_TYPES; meta?: any }>({ isOpen: false });
    const closeDrawer = () => {
        setDrawerData({ isOpen: false, type: undefined, meta: undefined });
    };
    const openDrawer = (type: DRAWER_TYPES, meta?: any) => {
        setDrawerData({ isOpen: true, type, meta });
    };

    const openFilterDrawer = () => {
        openDrawer(DRAWER_TYPES.FILTER);
    };

    const types = useMemo(() => {
        const dealTypes: string[] = [];
        for (const deal of deals) {
            if (deal.dealType.trim() && !dealTypes.includes(deal.dealType)) {
                dealTypes.push(deal.dealType);
            }
        }
        return dealTypes;
    }, [deals]);

    const statuses = useMemo(() => {
        const dealStatuses: string[] = [];
        for (const deal of deals) {
            if (deal.contractType.trim() && !dealStatuses.includes(deal.contractType)) {
                dealStatuses.push(deal.contractType);
            }
        }
        return dealStatuses;
    }, [deals]);

    const onChangeSearchFilter = (value: any) => {
        dispatch(getSearchDealFilterActions.success({ searchDealFilter: value }));
    };

    useEffect(() => {
        if (deals.length < ITEMS_PER_PAGE) {
            onChangeSearchFilter('');
            setDealStatusFilter([]);
            setDealTypesFilter([]);
            setDateFilter([null, null]);
        }
    }, [deals]); // eslint-disable-line

    useEffect(() => {
        if (getSearchDealValue !== undefined) {
            setSearchValue(getSearchDealValue);
        }
    }, [getSearchDealValue]);

    const filteredDeals: Deal[] = useMemo(() => {
        const [minDate, maxDate] = dateFilter;
        if (dealTypesFilter.length || dealStatusFilter.length || searchValue || (minDate && maxDate)) {
            return deals.filter(deal => {
                if (dealTypesFilter.length && !dealTypesFilter.includes(deal.dealType)) {
                    return false;
                }
                if (dealStatusFilter.length && !dealStatusFilter.includes(deal.contractType)) {
                    return false;
                }
                if (searchValue) {
                    const title = deal.title.toLowerCase();
                    const company = deal.company.toLowerCase();
                    const search = searchValue.toLowerCase();
                    return title.includes(search) || company.includes(search);
                }
                if (minDate && maxDate && deal.incomePeriod?.start) {
                    const compareDate = moment(deal.incomePeriod.start);
                    compareDate.isSameOrBefore(maxDate) && compareDate.isSameOrAfter(minDate);
                }
                return true;
            });
        }
        return deals;
    }, [searchValue, deals, dealTypesFilter, dateFilter, dealStatusFilter]);

    // pagination
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 9;
    const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
    const indexOfItemDeals = indexOfLastItem - ITEMS_PER_PAGE;
    const currentItems = filteredDeals.slice(indexOfItemDeals, indexOfLastItem);
    const pagesCount = Math.ceil(filteredDeals.length / ITEMS_PER_PAGE);

    useEffect(() => {
        if (filteredDeals.length < ITEMS_PER_PAGE) {
            setCurrentPage(1);
        }
    }, [filteredDeals]);

    return (
        <div className={css.module}>
            <div className={css.filtersContainer}>
                {deals.length > ITEMS_PER_PAGE && (filteredDeals.length > ITEMS_PER_PAGE || searchValue || dateFilter[0] || dateFilter[1]) && (
                    <div className={css.dateSearchContainer}>
                        <Search search={getSearchDealValue} onSearchChange={onChangeSearchFilter} className={css.search} />
                    </div>
                )}
                <ButtonBase>
                    <FilterListIcon className={css.filtersIcon} onClick={openFilterDrawer} />
                </ButtonBase>
            </div>
            <Box className={css.table}>
                <Grid container className={css.tableHeader} spacing={2}>
                    <Grid item xs={4}>
                        <Typography variant="h6">Deal</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="h6">Contacts</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="h6">Artist Manager</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3} className={css.tableBody}>
                    {currentItems.map((deal: Deal) => (
                        <Grid item xs={12} key={deal.assetCode}>
                            <Grid container className={css.tableRow} spacing={2}>
                            <Grid item xs={4}>
                                <DealCard data={deal} />
                            </Grid>
                            <Grid item xs={4} className={css.contacts}>
                                <Grid container spacing={2}>
                                    {deal.contacts
                                        .filter((contact: ContactsListItem) => contact.contactType !== 4)
                                        .map((contact: ContactsListItem) => (
                                            <ContactCard contact={contact} key={contact.contactCode || v4()} />
                                        ))}
                                </Grid>
                            </Grid>
                            <Grid item xs={4} className={css.contacts}>
                                <Grid container spacing={2}>
                                    {deal.contacts
                                        .filter((contact: ContactsListItem) => contact.contactType === 4)
                                        .map((contact: ContactsListItem) => (
                                            <ContactCard contact={contact} isManager key={contact.contactCode || v4()} />
                                        ))}
                                </Grid>
                            </Grid>
                        </Grid>
                        </Grid>
                    ))}
                </Grid>
            </Box>
            {filteredDeals.length > ITEMS_PER_PAGE && (
                <Box className={css.pagination}>
                    <Pagination
                        count={pagesCount}
                        page={currentPage}
                        onChange={(event: any, value: any) => {
                            setCurrentPage(value);
                        }}
                        variant="outlined"
                        shape="rounded"
                        hideNextButton
                        hidePrevButton
                    />
                </Box>
            )}
            <Box className={css.DrawerContainer}>
                <Drawer variant="persistent" anchor="right" open={drawerData.isOpen} id="deal-filter-drawer" className={css.rightDrawer}>
                    <Box className={css.contentWrapper}>
                        <Box className={css.closeDrawer}>
                            <IconButton onClick={closeDrawer}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography style={{ fontSize: '1rem' }}>Close</Typography>
                        </Box>
                        <Divider />
                        {drawerData.type === DRAWER_TYPES.FILTER && (
                            <div className={css.chipFilterContainer}>
                                <Typography variant="h6">Filters</Typography>
                                <LocalizationProvider dateAdapter={MomentUtils}>
                                    <DateRangePicker
                                        startText="Start Date"
                                        endText="End Date"
                                        value={dateFilter}
                                        inputFormat={selectedDateFormat}
                                        onChange={newValue => setDateFilter(newValue)}
                                        renderInput={(startProps, endProps) => (
                                            <React.Fragment>
                                                <TextField {...startProps} className={css.datePickerInput} variant="standard" />
                                                <DateRangeDelimiter> to </DateRangeDelimiter>
                                                <TextField {...endProps} className={css.datePickerInput} variant="standard" />
                                            </React.Fragment>
                                        )}
                                    />
                                </LocalizationProvider>
                                {types && (
                                    <div className={css.chipFilterWrapper}>
                                        <Box pb={1}>
                                            <Typography variant="body2" className={css.bold}>
                                                Deal type filters:
                                            </Typography>
                                        </Box>
                                        <ChipsFilter chips={types} filters={dealTypesFilter} onFilterChange={setDealTypesFilter} className={css.chipFilter} />
                                    </div>
                                )}
                                {statuses && (
                                    <div className={css.chipFilterWrapper}>
                                        <Box pb={1}>
                                            <Typography variant="body2" className={css.bold}>
                                                Deal status filters:
                                            </Typography>
                                        </Box>
                                        <ChipsFilter
                                            chips={statuses}
                                            filters={dealStatusFilter}
                                            onFilterChange={setDealStatusFilter}
                                            className={css.chipFilter}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </Box>
                </Drawer>
            </Box>
        </div>
    );
};
